import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Pencil, Mail, Phone, Shield, HardHat } from "lucide-react";
import { toast } from "sonner";
import Contractors from "@/pages/admin/Contractors";

const empty = { name: "", email: "", phone: "", role: "technician", password: "" };

function SystemUsers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/employees").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ name: e.name, email: e.email, phone: e.phone || "", role: e.role, password: "" }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) {
        const upd = { name: form.name, phone: form.phone, role: form.role };
        if (form.password) upd.password = form.password;
        await api.put(`/employees/${editId}`, upd);
      } else {
        await api.post("/employees", form);
      }
      toast.success("User saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete user?")) return; await api.delete(`/employees/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-slate-500">{items.length} login{items.length !== 1 ? "s" : ""} · admins &amp; direct technician logins</p>
        <Button data-testid="new-employee-button" onClick={openNew} className="bg-navy hover:bg-navy-light"><Plus className="w-4 h-4 mr-2" />Add Login</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((e) => (
          <div key={e.id} data-testid={`employee-card-${e.id}`} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center ${e.role === "admin" ? "bg-navy text-white" : "bg-blue-100 text-blue-700"}`}>
                  {e.role === "admin" ? <Shield className="w-5 h-5" /> : <HardHat className="w-5 h-5" />}
                </div>
                <div><p className="font-heading font-semibold text-navy">{e.name}</p><p className="text-xs text-slate-500 capitalize">{e.role}</p></div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`edit-employee-${e.id}`} onClick={() => openEdit(e)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                {e.role !== "admin" && <button data-testid={`delete-employee-${e.id}`} onClick={() => del(e.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
              <p className="flex items-center gap-2 truncate"><Mail className="w-4 h-4 text-slate-400" />{e.email}</p>
              {e.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{e.phone}</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Login" : "Add Login"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="employee-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
            <div><Label>Email *</Label><Input data-testid="employee-email-input" type="email" value={form.email} disabled={!!editId} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
            <div>
              <Label>Role</Label>
              <select data-testid="employee-role-select" value={form.role} onChange={(e) => set("role", e.target.value)} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 bg-white">
                <option value="technician">Technician</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <div><Label>{editId ? "New Password (leave blank to keep)" : "Password *"}</Label>
              <Input data-testid="employee-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="employee-save-button" onClick={save} className="bg-navy hover:bg-navy-light" disabled={!form.name || !form.email || (!editId && !form.password)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Employees() {
  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Team</h1>
        <p className="text-slate-500 mt-1">Onboard employees and contractors, and manage system logins</p>
      </div>

      <Tabs defaultValue="employees">
        <TabsList data-testid="team-tabs">
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="contractors" data-testid="tab-contractors">Contractors</TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system">System Access</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-6">
          <Contractors workerType="employee" />
        </TabsContent>

        <TabsContent value="contractors" className="mt-6">
          <Contractors workerType="contractor" />
        </TabsContent>

        <TabsContent value="system" className="mt-6">
          <SystemUsers />
        </TabsContent>
      </Tabs>
    </div>
  );
}
